import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { ApiWebhookCredential } from '../../models/appcloud/api-webhook-credential.model';
import { ErrorHandlingService } from '../../services/error-handling/error-handling.service';
import { SpinnerService } from '../../services/spinner';
import { environment } from '../../environments/environment';
import { HttpHeaderConstants } from '../../services/http-header.constant';

@Injectable({
    providedIn: 'root',
})
export class ApiWebhookCredentialService {
    readonly #errorHandlingService = inject(ErrorHandlingService);
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);

    private API_WEBHOOK_CREDENTIAL_URL: string = environment.publicApiHost + '/appcloud/api-webhook-credentials';

    create(apiWebhookCredential: ApiWebhookCredential, additionalHeaders: any = {}): Observable<ApiWebhookCredential> {
        this.#spinnerService.addProcess(
            'ApiWebhookCredentialService.create',
            'Creating your api webhook credential...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http
            .post<ApiWebhookCredential>(this.API_WEBHOOK_CREDENTIAL_URL, apiWebhookCredential, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while saving your credential. Please try again later.'
                    );
                    return of<ApiWebhookCredential>();
                }),
                finalize(() => {
                    this.#spinnerService.completeProcess('ApiWebhookCredentialService.create');
                })
            );
    }

    update(
        id: number,
        apiWebhookCredential: ApiWebhookCredential,
        additionalHeaders: any = {}
    ): Observable<ApiWebhookCredential> {
        this.#spinnerService.addProcess(
            'ApiWebhookCredentialService.update',
            'Updating your api webhook credential...'
        );
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.mergePatch,
                ...additionalHeaders,
            }),
        };

        return this.#http
            .patch<ApiWebhookCredential>(this.API_WEBHOOK_CREDENTIAL_URL + '/' + id, apiWebhookCredential, httpOptions)
            .pipe(
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while updating your credential. Please try again later.'
                    );
                    return of<ApiWebhookCredential>();
                }),
                finalize(() => {
                    this.#spinnerService.completeProcess('ApiWebhookCredentialService.update');
                })
            );
    }

    getById(id: number, additionalHeaders: any = {}): Observable<ApiWebhookCredential> {
        this.#spinnerService.addProcess(
            'ApiWebhookCredentialService.getById.' + id,
            'Loading your api webhook credential...'
        );
        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.get<ApiWebhookCredential>(`${this.API_WEBHOOK_CREDENTIAL_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while retrieving api webhook credential data from Relationship One. Please try again later.'
                );
                return of<ApiWebhookCredential>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ApiWebhookCredentialService.getById.' + id))
        );
    }

    delete(id: number, additionalHeaders: any = {}): Observable<any> {
        this.#spinnerService.addProcess(
            'ApiWebhookCredentialService.delete.' + id,
            'Deleting your api webhook credential...'
        );

        const httpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
        };

        return this.#http.delete<Observable<any>>(`${this.API_WEBHOOK_CREDENTIAL_URL}/${id}`, httpOptions).pipe(
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while deleting your webhook credential. Try again later.'
                );
                return of<Observable<any>>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ApiWebhookCredentialService.delete.' + id))
        );
    }
}
